import { Star, Bed, Bath, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";

const VillaCard = ({ villa, isFeatured = false, onBook }) => {
  return (
    <div 
      className={`villa-card group ${isFeatured ? "md:col-span-2 md:row-span-2" : ""}`}
      data-testid={`villa-card-${villa.id}`}
      onClick={() => onBook(villa)}
    >
      <img 
        src={villa.image} 
        alt={`${villa.name} - Luxury vacation rental in Juan Dolio, Dominican Republic`}
        className="transition-transform duration-500"
        loading="lazy"
      />
      <div className="villa-info">
        {isFeatured && (
          <Badge className="mb-2 sm:mb-3 bg-accent text-white border-0 text-xs">Most Popular</Badge>
        )}
        <h3 className="font-serif text-base sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">{villa.name}</h3>
        <p className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-3">{villa.tagline}</p>
        
        <div className="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-4 text-xs sm:text-sm text-white/90">
          <span className="flex items-center gap-1"><Bed size={14} />{villa.bedrooms} BR</span>
          <span className="flex items-center gap-1"><Bath size={14} />{Number.isInteger(villa.bathrooms) ? villa.bathrooms : villa.bathrooms.toFixed(1)} BA</span>
          <span className="flex items-center gap-1"><Users size={14} />{villa.guests}</span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-0.5 star-rating"><Star size={14} fill="currentColor" /><span className="text-white font-semibold text-sm">{villa.rating}</span></div>
            </div>
            <span className="text-white font-bold text-sm sm:text-base whitespace-nowrap">${villa.price_per_night}<span className="text-white/70 text-xs font-normal">/nt</span></span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onBook(villa); }}
            className="btn-accent py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm inline-flex items-center gap-1 flex-shrink-0"
            data-testid={`villa-book-${villa.id}`}
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

export const VillasSection = ({ villas, onBookVilla }) => {
  const { t } = useLanguage();
  const featuredVilla = villas.find(v => v.featured);
  const otherVillas = villas.filter(v => !v.featured);

  return (
    <section id="villas" className="section-padding bg-muted/30" data-testid="villas-section" role="region" aria-labelledby="villas-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-16">
          <Badge className="mb-3 sm:mb-4 bg-primary/10 text-primary border-primary/20">{t("nav.villas")}</Badge>
          <h2 id="villas-heading" className="font-serif text-2xl sm:text-3xl md:text-5xl font-bold text-foreground mb-2 sm:mb-4">{t("villas.title")}</h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto">{t("villas.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 auto-rows-[240px] sm:auto-rows-[300px]" role="list" aria-label="Available villas">
          {featuredVilla && <VillaCard villa={featuredVilla} isFeatured={true} onBook={onBookVilla} />}
          {otherVillas.map((villa) => (
            <VillaCard key={villa.id} villa={villa} onBook={onBookVilla} />
          ))}
        </div>
      </div>
    </section>
  );
};
